import { useCallback, useMemo, useRef, useState } from "react";

import {
  BasicRichText,
  type BasicRichTextProps,
  type EditorMenuItem,
} from "./BasicRichText";
import type { EditorProps } from "./editor";
import type { CapabilityName } from "./editor/types";
import { pickCapabilities } from "./editor/types";
import { getMentions } from "./getMentions";
import type { ChooserState } from "./presets/menuTypes";
import { presets } from "./presets/presets";

export type RichTextProps = {
  forType?: string;
  forId?: string;
  preset?: keyof typeof presets;
  // If preset === "custom", these capability keys filter the full preset
  customPresetExtensions?: CapabilityName[];
  customPresetMenu?: CapabilityName[];
  extraData?: unknown;
  fullMentionLinks?: boolean;
  /** Passed through to the editor (e.g. `onExternalLink` for embedded WebView bridges). */
  editorSettings?: EditorProps["settings"];
  onChange?: (html: string, textValue: string, extraData: unknown) => void;
} & BasicRichTextProps;

// forType and forId are required for uploads, however in cases such as the "simple" preset no upload is possible therefore they don't need to be added
// This is currently not enforced by the type system but can be gathered by the various examples of us using this component across the app
export const RichText = ({
  value,
  forType,
  forId,
  preset = "full",
  customPresetExtensions,
  customPresetMenu,
  onChange,
  extraData,
  fullMentionLinks,
  editorSettings,
  ...rest
}: RichTextProps) => {
  const [mentionState, setMentionState] = useState<{
    mentions: Record<string, unknown[]>;
    lastKind: string;
    lastQuery: string;
  } | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getThrottledMentions = useCallback((kind: string[], query: string) => {
    const kindKey = kind[0];
    if (!kindKey) return;
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    timeout.current = setTimeout(() => {
      getMentions(kind, query)
        .then((result) => {
          setMentionState({
            lastKind: kindKey,
            lastQuery: query,
            mentions: result,
          });
          timeout.current = null;
        })
        .catch(() => {});
    }, 300);
  }, []);

  const [chooser, setChooser] = useState<ChooserState>({});

  const setChooserType = useCallback(
    (kind: string, open: boolean, text?: string) =>
      setChooser(() => ({ [kind]: { open, text } })),
    [],
  );

  const handleChange = useCallback(
    (html: string, textValue: string) => {
      if (onChange) {
        return onChange(html, textValue, extraData);
      }
    },
    [onChange, extraData],
  );

  const presetChoice = useMemo(() => presets[preset], [preset]);
  const initialPresetMenu = useMemo(
    () => presetChoice.getMenu(chooser, setChooserType, forType, forId),
    [presetChoice, chooser, setChooserType, forType, forId],
  );

  const [capabilities, presetMenu] = useMemo(() => {
    let nextCapabilities = presetChoice.capabilities;
    let nextMenu = initialPresetMenu;
    if (preset === "custom" && customPresetExtensions) {
      nextCapabilities = pickCapabilities(
        nextCapabilities,
        customPresetExtensions,
      );
      nextMenu = (nextMenu as EditorMenuItem[]).filter((menu) =>
        (customPresetMenu || customPresetExtensions).includes(
          menu.name as CapabilityName,
        ),
      );
    }
    return [nextCapabilities, nextMenu];
  }, [
    preset,
    presetChoice,
    customPresetExtensions,
    customPresetMenu,
    initialPresetMenu,
  ]);

  return (
    <BasicRichText
      {...rest}
      value={value}
      capabilities={capabilities}
      mentions={mentionState?.mentions ?? {}}
      onSuggest={getThrottledMentions}
      extraMenuItems={presetMenu as EditorMenuItem[]}
      onChange={handleChange}
      settings={{
        ...(editorSettings ?? {}),
        ...(forType && forId ? { uploadOptions: { forType, forId } } : {}),
        fullMentionLinks: !!fullMentionLinks,
      }}
    />
  );
};

export const RichTextViewer = (
  props: Omit<BasicRichTextProps, "readOnly"> & {
    renderMention?:
      | ((args: {
          kind: string;
          id: string;
          label?: string;
          attrs: Record<string, unknown>;
        }) => React.ReactNode)
      | undefined;
  },
) => {
  return (
    <BasicRichText
      capabilities={presets.full.capabilities}
      {...props}
      readOnly={true}
      settings={{
        ...(props.settings || {}),
        renderMention: props.renderMention,
      }}
    />
  );
};

export const MarkdownViewer = (props: Omit<BasicRichTextProps, "readOnly">) => {
  return <RichTextViewer {...props} markdown={true} emptyReturnValue="" />;
};
